/**
 * Every word on the card a raid opens behind: the control legend, the call to play, and what a
 * failure to start says instead.
 *
 * Chrome. This is the last screen before the fiction and the only place a failure can be shown,
 * since console logging is banned by the lint rules, so it says what it means. See
 * [src/text/AGENTS.md](../../../text/AGENTS.md).
 *
 * Under the mode rather than in `@/text` because no other mode drops into a generated place, and a
 * shared legend would be a list every mode has to opt out of.
 */

import { actionKeyLabel, moveKeysLegend, weaponRowLegend } from '@/ui/keyLabels';

export const RAID_CARD_TITLE = 'Raid';
export const RAID_CARD_LEAVE = 'Back to menu';

/** The one failure the card can show, above whatever went wrong underneath it. */
export const RAID_CARD_FAILED = 'Cannot start the raid';

/**
 * Shown while the zone is being built and the physics runtime is loading.
 *
 * Both at once, deliberately: a player waiting does not care which of the two they are waiting on,
 * and two different words for the same second of black screen would only invite the question.
 */
export const RAID_CARD_LOADING = 'Building the zone';

/**
 * One line per stage of the build, so the wait reads as work rather than as a hang. The words are
 * about the place, not the code: nobody watching the card needs to know what a nav grid is.
 */
export const RAID_CARD_STAGES = {
  layout: 'Laying out the streets',
  buildings: 'Raising the ruins',
  streets: 'Pouring the tarmac',
  props: 'Scattering the wreckage',
  nav: 'Walking the ground',
  cover: 'Marking where to fight from',
  spawns: 'Placing what waits',
  validate: 'Proving the way out',
} as const;

/**
 * The attempt, named only when it is not the first: a zone that failed its checks is rebuilt off
 * a fresh salt, and a second try worth taking is worth admitting to.
 */
export function raidCardStageLine(stage: keyof typeof RAID_CARD_STAGES, attempt: number): string {
  const line = RAID_CARD_STAGES[stage];

  return attempt > 0 ? `${line} (attempt ${String(attempt + 1)})` : line;
}

/**
 * What a guest reads while the host is still choosing where the room is raiding.
 *
 * Its own line rather than the loading one, because nothing is being built yet: saying "building
 * the zone" to somebody waiting on another person is the card telling them the wrong thing to
 * blame.
 */
export const RAID_CARD_WAITING = 'Waiting for the host to pick a place';

export const RAID_CARD_READY_POINTER = 'Click to drop in';
export const RAID_CARD_READY_TOUCH = 'Tap to drop in';

/**
 * What a seed is, in one line, where somebody about to share one will read it.
 *
 * On the card rather than in a tooltip, because the seed is the only thing about a raid a player
 * can hand to somebody else, and a feature nobody knows is repeatable is a feature nobody uses.
 */
export const RAID_CARD_SEED_NOTE = 'Built from a seed. The same seed builds the same zone.';

/** What the zone holds, on the card, so nobody has to walk it to find out it is not empty. */
export function raidCardZoneLine(containers: number, ways: number): string {
  return `${String(containers)} containers to find, ${String(ways)} ways out.`;
}

/**
 * The key column reads the live bindings rather than spelling letters, on the terms
 * `rangeCardText.ts` states: fetched when the card is built, so a rebind is already on it.
 */
export function raidKeyControls(): readonly (readonly [string, string])[] {
  return [
    ['Mouse', 'look'],
    ['Left click', 'fire'],
    ['Right click', 'aim down sights'],
    [moveKeysLegend(), 'move'],
    [actionKeyLabel('sprint'), 'sprint'],
    [actionKeyLabel('crouch'), 'crouch'],
    [actionKeyLabel('jump'), 'jump'],
    [actionKeyLabel('cycleView'), 'first or third person'],
    [actionKeyLabel('swapShoulder'), 'swap shoulder'],
    [actionKeyLabel('reload'), 'reload'],
    [weaponRowLegend(), 'select weapon'],
    ['Esc', 'release the mouse'],
  ];
}

export const RAID_TOUCH_CONTROLS: readonly (readonly [string, string])[] = [
  ['Drag', 'look'],
  ['Left stick', 'move'],
  ['Fire', 'shoot, held for automatics'],
  ['Aim', 'aim down sights'],
  ['Load, Jump, Duck', 'reload, jump, crouch'],
  ['View', 'first or third person'],
  ['Menu', 'back to this card'],
];

/**
 * The two chips on the menu card.
 *
 * Here rather than at the card, because a fact is copy: `copy-check.mjs` is what enforces it, and
 * the labels are the part most likely to want rewording once there is a second thing to say about
 * a zone.
 */
export const RAID_FACT_WEAPONS = 'Guns';
export const RAID_FACT_PARTY = 'Players';
export const RAID_FACT_PARTY_VALUE = '1 to 4';
